import { useAuth } from 'react-oidc-context';
import { Tracking } from "@hqy/react-shared-components";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import { Theme, ThemeProvider } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Switch, useLocation } from "react-router-dom";
import GlobalStylesComponent from "./components/GlobalStylesComponent";
import Loading from "./components/Loading";
import { createCustomizedTheme } from "./components/MuiTheme";
import ProtectedRoute from "./components/ProtectedRoute";
import Routes from "./components/Routes";
import { Config } from "./Config";
import ErrorPage from "./pages/ErrorPage";
import HomePage from "./pages/HomePage";
import LogoutPage from "./pages/LogoutPage";
import SupportModalPage from "./pages/SupportModalPage";
import UserCredentialsApi from "./services/Api";
import { Brand } from "./services/Types";
import "./tyrian/tyrian-icons.css";
import NavBar from './components/NavBar';
import Header from './components/Header';
import Footer from './components/Footer';
import { useCookies } from 'react-cookie';
import { TimeoutProvider } from './components/TimeoutProvider';

const useStyles = makeStyles({
  app: {
    height: "100%",
  },
  maintenance: { textAlign: "center" }
});

const userCredentialsApi = new UserCredentialsApi();
const defaultTheme = createCustomizedTheme({});



function App() {
  const classes = useStyles();

  let query = useQuery();
  const userType = query.get("type") ? query.get("type") : undefined;
  const [cookies, setCookie] = useCookies(["credentialId"]);
  const [brand, setBrand] = useState<Brand>();
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [appLoading, setAppLoading] = useState(true);
  const [dataFetchingError, setDataFetchingError] = useState<Error | null>(
    null
  );

  const { error, signinRedirect  } = useAuth();  

  const search = useLocation().search;
  const searchParam = new URLSearchParams(search)
  const auth = useAuth();
  const token = auth.user?.access_token;
  const isLoading = auth.isLoading;

  let credentialId = searchParam.get("credential_id");   
     
  useEffect(() => {
    console.info(Config);
    
    // if credential id is provided in the query parameter, set the cookie 
    // to store credential id to be used after authentication to pull
    // data from UCA    
    // NOTE: This is a temporary fix until we can rework how credential id is passed to this application
    if(credentialId)
    {      
      setCookie("credentialId", credentialId, { path: '/', sameSite: 'lax', secure: true });
    }
    
    const init = async () => {
      await userCredentialsApi.init(token, signinRedirect );
      const brandResponse: Brand = await userCredentialsApi.getBranding();
      setBrand(brandResponse);
      const customTheme = createCustomizedTheme({
        actionItemColor: brandResponse.actionItemColor,
        actionItemHoverColor: brandResponse.actionItemHoverColor,
      });
      setTheme(customTheme);
    };
    if (error) {
      setDataFetchingError(error);
      console.error(error.message);
    } else if (!isLoading) {
      init().catch((error) => {
        setDataFetchingError(error);
        console.error(error.message);
      }).finally(() => {
        setAppLoading(false);
      });
    }
  }, [error, isLoading, signinRedirect ]);

  if (Config.app.maintenance) {
    return (
      <ThemeProvider theme={theme}>
        <Box className={classes.maintenance}>
          <img
            alt="maintenance"
            src="https://maintenance.healthequity.com/maintenance.png"
          />
        </Box>
      </ThemeProvider>
    );
  } else {
    return (
      <ThemeProvider theme={theme}>
        {appLoading && <Loading />}
        {!appLoading && (
          <>
            <main className="App">
              <CssBaseline />
              {GlobalStylesComponent}
              <TimeoutProvider>
              <Router>
                {dataFetchingError ? (
                  <ErrorPage error={dataFetchingError} />
                ) : (
                  <Switch>
                    <ProtectedRoute
                      path={Routes.home}
                      exact
                      component={HomePage}
                    />
                    <ProtectedRoute
                      path={Routes.supportModal}
                      component={() => <><NavBar userType={userType} credentialId={cookies.credentialId ?? ""} /><Header /><SupportModalPage brand={brand} credentialId={cookies.credentialId ?? ""}></SupportModalPage> <Footer/> </>}
                      exact
                    />
                    <Route path={Routes.logout} component={LogoutPage} />
                  </Switch>
                )}
              </Router>
              </TimeoutProvider>
            </main>
          </>
        )}
        <Tracking
          originClient={Config.auth_external.clientId}
          environmentCode={Config.environment.code}
          gtmId={Config.tracking.gtm}
        />
      </ThemeProvider>
    );
  }
}

export default App;
function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

